import React, { useState } from "react";
import Navbar from "./navbar";
import Footer from "./Footer";
import { Mail, Phone, MapPin, Globe, Send, ArrowRight, Sparkles } from "lucide-react";
import { FaWhatsapp, FaLinkedinIn } from "react-icons/fa";

export default function ContactPage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    service: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const subject = `Project Inquiry from ${form.name} — ${form.service}`;
    const body = `Name: ${form.name}\nEmail: ${form.email}\nCompany: ${form.company}\nService: ${form.service}\n\nMessage:\n${form.message}`;
    window.open(`https://mail.google.com/mail/?view=cm&to=info@orryworx.com&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, "_blank");
    setSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-white overflow-hidden">
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden bg-[#03152d] text-white pt-36 pb-20 px-6">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(52,152,245,0.12),transparent_70%)] pointer-events-none" />
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-[#355dff] opacity-20 blur-[120px] rounded-full pointer-events-none" />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 mb-6 px-5 py-2 rounded-full bg-white/5 border border-white/10 text-gray-300 text-xs font-semibold tracking-widest uppercase">
            <Sparkles size={14} className="text-cyan-400" /> Let's Build Together
          </div>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold leading-tight mb-6">
            <span className="bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400 bg-clip-text text-transparent">Start Your</span>
            <br />Project Today
          </h1>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto leading-8">
            Whether you're building an AI product, scaling your tech infrastructure, or need expert engineering talent — we'd love to hear about it.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="bg-[#F8F8FB] py-20 px-6">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-5 gap-12">

          {/* Left Info Panel */}
          <div className="lg:col-span-2 space-y-8">
            <div>
              <h2 className="text-2xl font-extrabold text-[#111827] mb-2">Get in Touch</h2>
              <p className="text-gray-500 text-[15px] leading-7">
                We typically respond within a few hours during business days. Let us know about your project and we'll set up a free consultation call.
              </p>
            </div>

            <div className="space-y-4">
              <a href="mailto:info@orryworx.com" className="flex items-center gap-4 p-4 bg-white border border-gray-200 rounded-2xl hover:border-[#6B5AE6] hover:shadow-md transition group">
                <div className="w-10 h-10 rounded-xl bg-[#F3F0FF] text-[#6B5AE6] flex items-center justify-center group-hover:bg-[#6B5AE6] group-hover:text-white transition">
                  <Mail size={18} />
                </div>
                <div>
                  <div className="text-[11px] text-gray-400 uppercase tracking-widest font-semibold">Email</div>
                  <div className="text-gray-900 font-semibold text-sm">info@orryworx.com</div>
                </div>
              </a>

              <a href="tel:+919911108696" className="flex items-center gap-4 p-4 bg-white border border-gray-200 rounded-2xl hover:border-[#6B5AE6] hover:shadow-md transition group">
                <div className="w-10 h-10 rounded-xl bg-[#F3F0FF] text-[#6B5AE6] flex items-center justify-center group-hover:bg-[#6B5AE6] group-hover:text-white transition">
                  <Phone size={18} />
                </div>
                <div>
                  <div className="text-[11px] text-gray-400 uppercase tracking-widest font-semibold">Phone</div>
                  <div className="text-gray-900 font-semibold text-sm">+91 9911108696</div>
                </div>
              </a>

              <a href="https://www.orryworx.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 p-4 bg-white border border-gray-200 rounded-2xl hover:border-[#6B5AE6] hover:shadow-md transition group">
                <div className="w-10 h-10 rounded-xl bg-[#F3F0FF] text-[#6B5AE6] flex items-center justify-center group-hover:bg-[#6B5AE6] group-hover:text-white transition">
                  <Globe size={18} />
                </div>
                <div>
                  <div className="text-[11px] text-gray-400 uppercase tracking-widest font-semibold">Website</div>
                  <div className="text-gray-900 font-semibold text-sm">www.orryworx.com</div>
                </div>
              </a>

              <div className="flex items-start gap-4 p-4 bg-white border border-gray-200 rounded-2xl">
                <div className="w-10 h-10 rounded-xl bg-[#F3F0FF] text-[#6B5AE6] flex items-center justify-center shrink-0">
                  <MapPin size={18} />
                </div>
                <div>
                  <div className="text-[11px] text-gray-400 uppercase tracking-widest font-semibold">Office</div>
                  <div className="text-gray-900 font-semibold text-sm leading-5">
                    1038 Sector 40, Gurgaon<br />Haryana, India – 122001
                  </div>
                </div>
              </div>
            </div>

            {/* Social */}
            <div>
              <p className="text-sm font-semibold text-gray-500 mb-3">Connect with us</p>
              <div className="flex gap-3">
                <a href="https://wa.me/919911108696" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-green-500 text-white flex items-center justify-center hover:bg-green-600 transition shadow-sm">
                  <FaWhatsapp size={16} />
                </a>
                <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700 transition shadow-sm">
                  <FaLinkedinIn size={16} />
                </a>
              </div>
            </div>
          </div>

          {/* Right Form */}
          <div className="lg:col-span-3">
            {submitted ? (
              <div className="bg-white border border-gray-200 rounded-3xl p-12 text-center">
                <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
                  <Send size={28} className="text-emerald-600" />
                </div>
                <h3 className="text-2xl font-extrabold text-[#111827] mb-3">Message Sent!</h3>
                <p className="text-gray-500">Your inquiry has been sent to our team. We'll get back to you within 24 hours.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-white border border-gray-200 rounded-3xl p-8 shadow-sm space-y-5 font-[inherit]">
                <h3 className="text-xl font-extrabold text-[#111827] mb-2">Send us a Message</h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-widest block mb-1.5">Full Name *</label>
                    <input
                      name="name" required value={form.name} onChange={handleChange}
                      placeholder="John Doe"
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 text-gray-900 text-sm focus:outline-none focus:border-[#6B5AE6] focus:ring-2 focus:ring-[#6B5AE6]/10 transition font-[inherit]"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-widest block mb-1.5">Email *</label>
                    <input
                      name="email" type="email" required value={form.email} onChange={handleChange}
                      placeholder="john@company.com"
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 text-gray-900 text-sm focus:outline-none focus:border-[#6B5AE6] focus:ring-2 focus:ring-[#6B5AE6]/10 transition font-[inherit]"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-widest block mb-1.5">Company / Organization</label>
                  <input
                    name="company" value={form.company} onChange={handleChange}
                    placeholder="Acme Corp."
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 text-gray-900 text-sm focus:outline-none focus:border-[#6B5AE6] focus:ring-2 focus:ring-[#6B5AE6]/10 transition font-[inherit]"
                  />
                </div>

                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-widest block mb-1.5">Service Interested In</label>
                  <select
                    name="service" value={form.service} onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 text-gray-900 text-sm focus:outline-none focus:border-[#6B5AE6] focus:ring-2 focus:ring-[#6B5AE6]/10 transition bg-white font-[inherit]"
                  >
                    <option value="">Select a service...</option>
                    <option>AI Development (LLMs, RAG, AI Agents)</option>
                    <option>Computer Vision</option>
                    <option>Enterprise Software Development</option>
                    <option>Web Development</option>
                    <option>Mobile App Development</option>
                    <option>Cloud & DevOps</option>
                    <option>IT Consultancy</option>
                    <option>Product Demo (DocLift, RailPlot, etc.)</option>
                  </select>
                </div>

                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-widest block mb-1.5">Message *</label>
                  <textarea
                    name="message" required value={form.message} onChange={handleChange}
                    rows={5} placeholder="Tell us about your project, timeline, and budget..."
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 text-gray-900 text-sm focus:outline-none focus:border-[#6B5AE6] focus:ring-2 focus:ring-[#6B5AE6]/10 transition resize-none font-[inherit]"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full flex items-center justify-center gap-3 bg-gradient-to-r from-blue-600 to-[#6B5AE6] text-white font-semibold py-4 rounded-xl hover:shadow-lg hover:shadow-[#6B5AE6]/30 hover:scale-[1.01] transition duration-300 font-[inherit]"
                >
                  <Send size={18} /> Send Message
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
